/**
 * EmbeddedToolProvider — Derives app-scoped tools from window.__cogo_tools__.
 *
 * Polls window.__cogo_tools__.getManifest() until tools are found or maxAttempts
 * reached. Limits tools to 20 max to avoid Gemini function declaration limit
 * (Pitfall 4). Exposes tools/toolCount/loading via React context.
 */

import { useState, useEffect, createContext, useContext, type ReactNode } from 'react'

interface EmbeddedToolsContext {
  tools: Array<{ name: string; description?: string }>
  toolCount: number
  loading: boolean
}

const EmbeddedToolsCtx = createContext<EmbeddedToolsContext>({
  tools: [],
  toolCount: 0,
  loading: true,
})

export function useEmbeddedTools() {
  return useContext(EmbeddedToolsCtx)
}

interface EmbeddedToolProviderProps {
  children: ReactNode
}

export function EmbeddedToolProvider({ children }: EmbeddedToolProviderProps) {
  const [tools, setTools] = useState<Array<{ name: string; description?: string }>>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    let attempts = 0
    const maxAttempts = 15 // 3 seconds max (15 * 200ms)

    function derive() {
      const cogo = (window as any).__cogo_tools__
      const manifest = cogo?.getManifest?.() ?? []
      if (manifest.length > 0 || attempts >= maxAttempts) {
        if (!cancelled) {
          // Show all registered tools in UI header (Gemini filtering is done by persona.toolFilter)
          setTools(manifest)
          setLoading(false)
        }
      } else {
        attempts++
        setTimeout(derive, 200)
      }
    }

    derive()
    return () => { cancelled = true }
  }, [])

  return (
    <EmbeddedToolsCtx.Provider value={{ tools, toolCount: tools.length, loading }}>
      {children}
    </EmbeddedToolsCtx.Provider>
  )
}
